import { LocationQuery } from "vue-router";
import {
  getCartItemWithLicenseKey,
  getLicenseKeyFromCartItems,
  getPlanetOpenTextDiscountStatus,
  getProspectId,
  getQueryParam,
  redirectToOutstandingInvoice,
  setEmailCartParameters,
  setupCartForUser,
  setupCartNewUser,
  loadPaymentMethods,
  setShippingAddressFromCC,
  handlePurchaseError,
  getRenewDate,
  formatMoney,
} from "@/components/Buy/BuyHelper";
import {
  enhancedCartTypes,
  openTextBrands,
  pageStates,
  paymentTypes,
  payPalGateways,
  PlanetOTErrors,
  purchaseErrors,
} from "@/components/Buy/BuyEnums";
import {
  bazaarVoiceScriptCarboniteUrl,
  bazaarVoiceScriptWebrootUrl,
  buyFlowPageTwoRoute,
  DEFAULT_SKU,
  DEFAULT_TERM_LENGTH_MONTHS,
  FLAGS,
  HTTP_STATUS_NOT_FOUND,
  InvalidOrderCode,
  PURCHASE_KEYCODE_DELAY_IN_SECOND,
  UNITED_STATES,
  UNITED_STATES_COUNTRY_CODE,
} from "@/define";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { useBuyFlowManagerStore } from "@/stores/buyFlowManager";
import { useUserStore } from "@/stores/user";
import { AxiosError, AxiosResponse } from "axios";
import { IPurchaseError, ISetupCartRequest } from "@/components/Buy/Interfaces";
import { handleApiError } from "@/common/handleApiError";
import router from "@/routes";
import { isSafeSubscriptionClosed } from "@/common/subscriptionHelper";
import { useUnauthenticatedBuyFlowStore } from "@/stores/unauthorizedBuyflow";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { logEvent, logException } from "@/common/logger";
import { unifiedApi } from "@/common";
import { copyStringWithoutReference, sortArrayOfObjects } from "../Buy/commonFn";
import { cartUpdate, googleTagCartEvents } from "@/common/googleTagEvents";
import { clearSession } from "@/common/logout";
import { NotificationType, useNotificationsStore } from "@/stores/notifications";
import { usePaymentPageStore } from "@/stores/paymentPage";
import { pageStates as paymentPageStates } from "@/components/Buy/Payment/PaymentConfigs"; //Old Payment Folder
import { bazaarVoiceInitialize, bvCarboniteEventHandler, bvWebrootEventHandler } from "@/common/bazaarVoiceEvents";
import {
  IPurchaseKeycode,
  IUserAddress,
  ILicenseKeyInfo,
  IShoppingCart,
  IPaymentMethod,
} from "@/common/api/unifiedPortal/interfaces";
import { config, layoutItemNames } from "@/components/BuyEnhancedCart/DynamicComponents/Config";
import { useEmailPageStore } from "@/stores/emailPage";
import {
  isAddress1Valid,
  isCityValid,
  isFirstNameValid,
  isLastNameValid,
  isStateValid,
  isZipValid,
} from "./ShippingForm/ShippingFormValidation";
import { t } from "@/i18n";
import { addBuyFlowLogEntry } from "@/common/buyflowTracker";
import { shippingStatesAndProvinces, statesAndProvincesCountryCodes } from "@/common/ShippingStatesAndProvinces";
import { hideAllWisepopsPopups, setWisepopsRevenue } from "@/common/wisepops";

export function checkQueryParams(params: LocationQuery) {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();

  buyFlowManagerStore.sku = getQueryParam(params, "sku") || "";
  buyFlowManagerStore.ratePlanId = getQueryParam(params, "ratePlanId") || getQueryParam(params, "rpi") || "";
  buyFlowManagerStore.prospectGuid = getQueryParam(params, "guid") || getQueryParam(params, "pguid") || "";
  buyFlowManagerStore.orderCode = getQueryParam(params, "orderCode") || getQueryParam(params, "oc") || "";
  buyFlowManagerStore.step = getQueryParam(params, "step") || pageStates.email.toString(); //Can't short hand do to navigation
  buyFlowManagerStore.deviceId = parseInt(getQueryParam(params, "deviceId") || getQueryParam(params, "did") || "0");
  buyFlowManagerStore.userGuid = getQueryParam(params, "userGuid") || getQueryParam(params, "ug") || "";
  buyFlowManagerStore.subscriptionMonths =
    parseInt(getQueryParam(params, "subscriptionMonths") || getQueryParam(params, "sm") || "") ||
    DEFAULT_TERM_LENGTH_MONTHS;
  buyFlowManagerStore.quantity = parseInt(getQueryParam(params, "quantity") || getQueryParam(params, "qty") || "1");
  buyFlowManagerStore.userId = parseInt(getQueryParam(params, "userId") || getQueryParam(params, "uid") || "") || 0;
  buyFlowManagerStore.promoCode = getQueryParam(params, "couponcode") || getQueryParam(params, "cc") || "";
  buyFlowManagerStore.catId = parseInt(getQueryParam(params, "catid") || getQueryParam(params, "cid") || "0");
  buyFlowManagerStore.subscriptionNumber =
    getQueryParam(params, "subscriptionnumber") || getQueryParam(params, "sn") || "";
  buyFlowManagerStore.includedUnits = parseInt(getQueryParam(params, "iu") || "1");
  buyFlowManagerStore.emailAc = parseInt(getQueryParam(params, "ac")) || null;
  buyFlowManagerStore.emailRc = parseInt(getQueryParam(params, "rc")) || null;
  buyFlowManagerStore.emailEmcid = parseInt(getQueryParam(params, "emcid")) || null;
  buyFlowManagerStore.emailUtmContent = getQueryParam(params, "utm_content") || null;
  buyFlowManagerStore.emailUtmMedium = getQueryParam(params, "utm_medium") || null;
  buyFlowManagerStore.emailUtmSource = getQueryParam(params, "utm_source") || null;
  buyFlowManagerStore.emailUtmTerm = getQueryParam(params, "utm_term") || null;
  buyFlowManagerStore.emailUtmCampaign = getQueryParam(params, "utm_campaign") || null;
  buyFlowManagerStore.enrollmentGuid = getQueryParam(params, "eg");
  buyFlowManagerStore.licenseKey = getQueryParam(params, "lk");
  buyFlowManagerStore.vendorKey = getQueryParam(params, "sn");
  buyFlowManagerStore.loadCartFromDB = getQueryParam(params, "lcd") === "1";
  buyFlowStore.referralUrl =
    getQueryParam(params, "referralurl") || getQueryParam(params, "rurl") || buyFlowStore.referralUrl || "";
  buyFlowManagerStore.loadSessionStorageCart = getQueryParam(params, "lsc") === "1";
  buyFlowStore.openTextBrand = openTextBrands.Webroot; //Set the brand to Webroot for enhanced cart
}

//Gets the config for the buy flow layout
//Attempts to pull/merge values from the DB
//If the Db call fails it defaults to the local version
export async function getBuyFlowConfig() {
  try {
    const dbConfig = (await unifiedApi.getBuyFlowConfig()).data;
    const localConfig = config;

    for (const key in dbConfig) {
      dbConfig[key].forEach(element => {
        //Find the config setting in the local config
        const localItem = localConfig[key].find(l => l.name === element.name);
        if (localItem) {
          //Set the values from the DB config
          localItem.column = element.column;
          localItem.position = element.position;
        }
      });
    }

    return localConfig;
  } catch {
    //Return the config we have if API call fails
    return config;
  }
}

export function setBuyFlowSteps() {
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const items = buyFlowManagerStore.layout.filter(l => l.stepOrder);
  const sortedItems = sortArrayOfObjects(items, "stepOrder", "ascending");
  buyFlowManagerStore.buyflowSteps.push(...sortedItems);
}

export async function setupCartForPlanetOT(castleRequestToken: string) {
  const buyFlowStore = useBuyFlowStore();
  const userStore = useUserStore();
  const notificationsStore = useNotificationsStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();
  try {
    buyFlowStore.openTextBrand = openTextBrands.Webroot;
    //Check if user is logged in and log them out if they are
    if (userStore.currentUser) {
      await clearSession();
      await unifiedApi.logout();
      userStore.resetState();
    }

    buyFlowStore.pageState = pageStates.email;
    const cart = (
      await unifiedApi.getShoppingCartByEnrollmentGuid(buyFlowManagerStore.enrollmentGuid, castleRequestToken)
    ).data;
    cart.items.forEach(l => {
      l.autoRenew = true;
    });
    buyFlowStore.cart = cart;
    buyFlowStore.planetOpenTextDiscountApplied = true;
    buyFlowStore.currentEmail = cart.userEmail;
    buyFlowManagerStore.catId = cart.campaignId;
    await buyFlowStore.populatePlans(buyFlowManagerStore.catId, false);
    buyFlowStore.planetOpenTextDiscountApplied = true;
  } catch (err) {
    const error = err as AxiosError;
    const response = error.response?.data as IPurchaseError;
    const errorMessage = response.message;

    if (
      errorMessage === PlanetOTErrors.GuidAlreadyUsed ||
      errorMessage === PlanetOTErrors.GuidExpired ||
      errorMessage === PlanetOTErrors.InvalidGuid
    ) {
      notificationsStore.addNotification({ type: "PlanetOpenTextEnrollmentGuidError" });
    } else {
      notificationsStore.addNotification({ type: "PlanetOpenTextInvalidCart" });
    }
  }
}

export async function setupCartFromOrderCode(
  isRenewOrUpgrade: boolean,
  shoppingCartByOrderCode: AxiosResponse<IShoppingCart> | null
) {
  const buyFlowStore = useBuyFlowStore();
  const subscriptionStore = useSubscriptionsStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const userStore = useUserStore();
  if (!shoppingCartByOrderCode) return;
  try {
    const cart = shoppingCartByOrderCode.data;
    buyFlowStore.cart = cart;

    if (cart && cart.campaignId > 0 && !buyFlowStore.isRenewOrUpgrade) {
      buyFlowManagerStore.catId = cart.campaignId;
      await buyFlowStore.populatePlans(buyFlowManagerStore.catId, isRenewOrUpgrade, false, true);
    }

    if (!buyFlowStore.userGuid) {
      buyFlowStore.userGuid = buyFlowManagerStore.userGuid;
    }

    //Check cart of license key or use query parameter
    buyFlowStore.licenseKey = getLicenseKeyFromCartItems(cart?.items || []) || buyFlowManagerStore.licenseKey;
    if (buyFlowStore.licenseKey) {
      const cartItemWithLicenseKey = getCartItemWithLicenseKey(cart?.items);
      //Get license key info for renewal, need to figure out how to do that for an upgrade
      buyFlowStore.setLicenseKeyInfo(
        (await unifiedApi.getLicenseKeyInfo(buyFlowStore.licenseKey, cartItemWithLicenseKey?.sku || "")).data
      );

      if (buyFlowStore.licenseKeyInfo?.hasOutstandingInvoice) {
        redirectToOutstandingInvoice(true);
        return;
      }
    }

    //if there is a promo code add it to the cart
    if (buyFlowManagerStore.promoCode && buyFlowStore.cart) {
      buyFlowStore.cart.promotionCode = buyFlowManagerStore.promoCode;
      await buyFlowStore.updateShoppingCartTotal();
    }

    cartUpdate(cart, pageStates.email);
    if (userStore.currentUser && userStore.isEstablishedAccount) {
      await subscriptionStore.populateSubscriptions();
    }
    buyFlowStore.planetOpenTextDiscountApplied = getPlanetOpenTextDiscountStatus();
  } catch (err) {
    logEvent("Error on getting cart by orderCode", JSON.stringify(err as AxiosError));
    const axiosError = err as AxiosError;
    const response = axiosError.response?.data as IPurchaseError;
    // if Webroot, clear the cart and show Start Shopping button
    if (buyFlowManagerStore.isWebrootPurchase && response.message === InvalidOrderCode) {
      await buyFlowStore.clearCart(openTextBrands.Webroot, true);
      await buyFlowStore.updateShoppingCartTotal();
      return;
    }
  }
}

export async function setupCartFromDB() {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();

  try {
    await buyFlowStore.getShoppingCart(buyFlowManagerStore.userGuid);

    buyFlowStore.userGuid = buyFlowManagerStore.userGuid;
    //If there is a promo code add it to the cart
    if (buyFlowManagerStore.promoCode && buyFlowStore.cart) {
      buyFlowStore.cart.promotionCode = buyFlowManagerStore.promoCode;
      await buyFlowStore.updateShoppingCartTotal();
    }
  } catch (err) {
    logEvent("Error on getting cart", JSON.stringify(err as AxiosError));
    if (buyFlowManagerStore.isWebrootPurchase) {
      await buyFlowStore.clearCart(openTextBrands.Webroot, true);
    }
  }
}

export async function setupCartFromSessionStorage() {
  const buyFlowStore = useBuyFlowStore();
  const subscriptionStore = useSubscriptionsStore();
  const userStore = useUserStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const emailPageStore = useEmailPageStore();

  buyFlowStore.cart = JSON.parse(sessionStorage["BuyFlow_Session_Cart"]);

  // Should update cart if it has an Allstate item
  if (buyFlowStore.hasAllStateInCart) {
    await buyFlowStore.updateShoppingCartTotal();
  }
  if (userStore.currentUser && userStore.isEstablishedAccount) {
    await subscriptionStore.populateSubscriptions();
    emailPageStore.email = userStore.currentUser.email ?? userStore.email;
    buyFlowManagerStore.goToNextStep();
  }
  buyFlowStore.planetOpenTextDiscountApplied = getPlanetOpenTextDiscountStatus();
}

export function verifyQueryParams() {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();

  // Check if sku and months on queryString are valid
  //This check is only for Carbonite products
  if (buyFlowManagerStore.sku && buyFlowManagerStore.subscriptionMonths && !buyFlowManagerStore.isWebrootPurchase) {
    const ratePlan = buyFlowStore.getRatePlanIdBySku(buyFlowManagerStore.sku, buyFlowManagerStore.subscriptionMonths);
    if (!ratePlan) {
      buyFlowManagerStore.sku = buyFlowStore.isSkuValid(buyFlowManagerStore.sku)
        ? buyFlowManagerStore.sku
        : "PersonalPlus";
      buyFlowManagerStore.subscriptionMonths = 12;
    } else if (buyFlowManagerStore.ratePlanId && buyFlowManagerStore.ratePlanId != ratePlan) {
      // if ratePlanId passed in URL is different (invalid) then productCatalog, override.
      buyFlowManagerStore.ratePlanId = ratePlan;
    }
  } else if (
    buyFlowManagerStore.sku &&
    buyFlowManagerStore.subscriptionMonths &&
    buyFlowManagerStore.isWebrootPurchase
  ) {
    //Set defaults for Webroot here
    //Default for WR sku will be an error message
    const skuRatePlans = buyFlowStore.getRatePlansForSku(buyFlowManagerStore.sku);
    let seats = skuRatePlans.find(l => l.includedUnits === buyFlowManagerStore.includedUnits)?.includedUnits;

    if (!seats && skuRatePlans.length > 0) {
      const sortedRatePlans = sortArrayOfObjects(skuRatePlans, "months", "ascending");
      seats = sortedRatePlans[0].includedUnits;

      if (seats) {
        buyFlowManagerStore.includedUnits = seats;
      }
    }
  }
}

export async function verifyLicenseKeyInfo(licenseKeyInfo: AxiosResponse<ILicenseKeyInfo> | null) {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const subscriptionStore = useSubscriptionsStore();
  const userStore = useUserStore();

  try {
    buyFlowStore.licenseKey = copyStringWithoutReference(buyFlowManagerStore.licenseKey);
    let hasOutstandingInvoice = false;
    // for upgrade using rateplanId
    if (!buyFlowManagerStore.sku && buyFlowManagerStore.ratePlanId) {
      buyFlowManagerStore.sku =
        buyFlowStore.plans.find(p => p.ratePlans.some(r => r.ratePlanId == buyFlowManagerStore.ratePlanId))?.sku ?? "";
    }
    const sub = subscriptionStore.subscriptions.find(l => l.licenseKey === buyFlowManagerStore.licenseKey);
    if (sub && sub.hasOutstandingInvoices) {
      hasOutstandingInvoice = true;
    } else {
      if (buyFlowManagerStore.sku && buyFlowManagerStore.licenseKey) {
        buyFlowStore.setLicenseKeyInfo(
          (await unifiedApi.getLicenseKeyInfo(buyFlowManagerStore.licenseKey, buyFlowManagerStore.sku)).data
        );
      } else if (licenseKeyInfo?.data) {
        buyFlowStore.setLicenseKeyInfo(licenseKeyInfo.data);
      }
    }

    if (hasOutstandingInvoice || buyFlowStore.licenseKeyInfo?.hasOutstandingInvoice) {
      redirectToOutstandingInvoice(!userStore.currentUser);
      return;
    }

    //If this is an S2 recapture we need to get the product catalog again with the catId to get correct prices
    if (buyFlowStore.licenseKeyInfo?.isBestBuyS2Recapture) {
      await buyFlowStore.populatePlans(buyFlowManagerStore.catId, false, false, true);
    }
  } catch (err) {
    logEvent("Error on license key info", JSON.stringify(err as AxiosError));
    // clear the cart and show Start Shopping button
    await buyFlowStore.clearCart(openTextBrands.Webroot, true);
    //Throw error so caller can turn off loading spinner
    throw err;
  }
}

//This will look at all the data and setup the cart
export async function setupCart() {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const subscriptionStore = useSubscriptionsStore();
  const userStore = useUserStore();
  const unauthenticatedBuyflowStore = useUnauthenticatedBuyFlowStore();

  let newUser = true;
  buyFlowStore.userGuid = buyFlowManagerStore.userGuid;

  const request = {
    ratePlanId: buyFlowManagerStore.ratePlanId,
    sku: buyFlowManagerStore.sku,
    step: buyFlowManagerStore.step,
    deviceId: buyFlowManagerStore.deviceId,
    userGuid: buyFlowManagerStore.userGuid,
    subscriptionMonths: buyFlowManagerStore.subscriptionMonths,
    quantity: buyFlowManagerStore.quantity,
    subscriptionNumber: buyFlowManagerStore.subscriptionNumber,
    includedUnits: buyFlowManagerStore.includedUnits,
    licenseKey: buyFlowManagerStore.licenseKey,
    isWebroot: buyFlowManagerStore.isWebrootPurchase,
  } as ISetupCartRequest;

  //We already have a logged in user or userGuid
  if (
    userStore.currentUser ||
    buyFlowManagerStore.licenseKey ||
    buyFlowManagerStore.userGuid ||
    buyFlowManagerStore.deviceId > 0
  ) {
    newUser = false;
    //Populate the subscriptions store is we have an established User. A preliminary user will never have subs
    if (userStore.currentUser && userStore.isEstablishedAccount) {
      await subscriptionStore.populateSubscriptions();

      if (buyFlowManagerStore.deviceId > 0) {
        const sub = subscriptionStore.subscriptions.find(l => l.computer?.id === buyFlowManagerStore.deviceId);
        if (sub && isSafeSubscriptionClosed(sub)) {
          router.push({ path: "/buy/error/unrecoverable" });
          return;
        }

        if (
          subscriptionStore.subscriptions.find(l => l.computer?.id === buyFlowManagerStore.deviceId)
            ?.hasOutstandingInvoices
        ) {
          redirectToOutstandingInvoice();
          return;
        }
      } else if (buyFlowManagerStore.licenseKey) {
        const sub = subscriptionStore.subscriptions.find(l => l.licenseKey === buyFlowManagerStore.licenseKey);

        if (sub && sub.hasOutstandingInvoices) {
          redirectToOutstandingInvoice();
          return;
        }
      }
    } else if (buyFlowManagerStore.deviceId > 0) {
      //Get the subscription for the given device / userGuid
      try {
        await unauthenticatedBuyflowStore.populateSubscription(buyFlowManagerStore.deviceId, true);

        if (unauthenticatedBuyflowStore?.subscription?.hasOutstandingInvoices) {
          redirectToOutstandingInvoice(true);
          return;
        }
      } catch (error) {
        const err = error as AxiosError;
        const response = err?.response?.data as IPurchaseError;
        //Device has been purged error
        if (response && response?.message === purchaseErrors.NotEligibleForRenewal) {
          router.push({ path: "/buy/error/unrecoverable" });
          return;
        }
      }
    }

    if (!newUser) {
      await setupCartForUser(request);
      if (buyFlowManagerStore.userGuid) {
        if (buyFlowStore.cart) {
          buyFlowStore.cart.userGuid = buyFlowManagerStore.userGuid;
        }
      }
    }
  }

  if (newUser) {
    setupCartNewUser(request);
  }

  if (buyFlowStore.cart) {
    // pass empty string if the cart doesn't have a keycode (otherwise the cart will keep whatever it has in database)
    buyFlowStore.cart.promotionCode = buyFlowManagerStore.promoCode ?? "";

    // set catId to 0 if null (otherwise the cart will keep whatever it has in database)
    buyFlowStore.cart.campaignId = buyFlowManagerStore.catId ?? 0;
  }

  //If prospectGuid exist in query params then fetch the prospectId from API and save to Buyflow store
  if (buyFlowManagerStore.prospectGuid != "" && buyFlowStore.cart) {
    const prospectId = (await getProspectId(buyFlowManagerStore.prospectGuid)) ?? 0;
    buyFlowStore.cart.prospectId = prospectId;
  }

  try {
    setEmailCartParameters(
      buyFlowManagerStore.emailAc,
      buyFlowManagerStore.emailRc,
      buyFlowManagerStore.emailEmcid,
      buyFlowManagerStore.emailUtmContent,
      buyFlowManagerStore.emailUtmMedium,
      buyFlowManagerStore.emailUtmSource,
      buyFlowManagerStore.emailUtmTerm,
      buyFlowManagerStore.emailUtmCampaign
    );
    await buyFlowStore.updateShoppingCartTotal(false, true);
  } catch (e) {
    const err = e as AxiosError;
    if (err.response?.status === HTTP_STATUS_NOT_FOUND) {
      buyFlowManagerStore.userGuid = "";
      buyFlowStore.userGuid = "";
      buyFlowStore.pageState = pageStates.email;
    } else {
      handleErrorOnUpdate(err);
    }
  }
}

// For some preview errors we want to retry the preview call with a modified cart
// This method modifies the cart depending on the error
export async function handleErrorOnUpdate(error) {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();

  let retryPreview = false;
  const axiosError = error as AxiosError;
  if (buyFlowStore.cart) {
    const response = axiosError.response?.data as IPurchaseError;

    if (
      (response.message === purchaseErrors.InvalidComputer ||
        response.message === purchaseErrors.SmbComputerIneligible) &&
      buyFlowManagerStore.deviceId > 0
    ) {
      // If deviceId query string is greater then 0 and is invalid
      // Override all computerIds in the items = 0
      buyFlowManagerStore.deviceId = 0;
      for (const item of buyFlowStore.cart.items) {
        item.computerId = 0;
      }

      retryPreview = true;
    } else if (
      response.message === purchaseErrors.InvalidPromotionCode &&
      (buyFlowManagerStore.promoCode || buyFlowStore.cart.promotionCode)
    ) {
      // If promoCode query string is passed and is invalid
      // Override promotionCode to empty
      buyFlowManagerStore.promoCode = "";
      buyFlowStore.cart.promotionCode = "";
      retryPreview = true;
    } else if (response.message === purchaseErrors.InvalidSubscription && buyFlowManagerStore.subscriptionNumber) {
      // If subscriptionNumber is passed then 0 and is invalid
      // Override subscriptionNumbers to empty
      buyFlowManagerStore.subscriptionNumber = "";
      for (const item of buyFlowStore.cart.items) {
        item.subscriptionNumbers = [];
      }
      retryPreview = true;
    }
  }
  if (retryPreview) {
    await buyFlowStore.updateShoppingCartTotal(false, true);
  } else {
    handleApiError(axiosError, false);
  }
}

export async function handleEmptyCartAfterSetup() {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();

  if (!buyFlowStore.cart || buyFlowStore.cart?.items.length === 0) {
    if (buyFlowManagerStore.licenseKey || buyFlowManagerStore.isWebrootPurchase) {
      await buyFlowStore.clearCart(openTextBrands.Webroot, true);
    } else {
      const ratePlanId = buyFlowStore.getRatePlanIdBySku(
        buyFlowManagerStore.sku || DEFAULT_SKU,
        buyFlowManagerStore.subscriptionMonths || DEFAULT_TERM_LENGTH_MONTHS,
        buyFlowManagerStore.includedUnits || 1
      );
      const beginShoppingCartRequest = {
        sku: buyFlowManagerStore.sku || DEFAULT_SKU,
        ratePlanId: ratePlanId,
        quantity: buyFlowManagerStore.quantity,
        deviceId: buyFlowManagerStore.deviceId,
        subscriptionNumber: buyFlowManagerStore.subscriptionNumber,
        step: buyFlowManagerStore.step,
      };
      buyFlowStore.beginShoppingCart(beginShoppingCartRequest);
      try {
        await buyFlowStore.updateShoppingCartTotal(false, true);
      } catch (error) {
        await handleErrorOnUpdate(error);
      }
    }
  }
}

export function checkShippingFormForErrors(validateKey?: string, isCountryUSA = false) {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const skipValidation = ["address2"];

  if (validateKey) {
    buyFlowStore.showShippingFormErrors[validateKey] = validateAddressFormFields(
      validateKey,
      buyFlowStore.shippingAddress[validateKey],
      isCountryUSA
    );
  } else {
    for (const key in buyFlowStore.shippingAddress) {
      //skip form items that are not required
      if (skipValidation.indexOf(key) >= 0) {
        continue;
      }

      //check for valid format
      buyFlowStore.showShippingFormErrors[key] = validateAddressFormFields(
        key,
        buyFlowStore.shippingAddress[key],
        isCountryUSA
      );
    }
  }

  const shippingFormHasErrors = Object.values(buyFlowStore.showShippingFormErrors).some(l => l.length > 0);
  buyFlowManagerStore.shippingFormHasErrors = shippingFormHasErrors;
  return shippingFormHasErrors;
}

export function checkPaymentFormForErrors(validateKey?: string, isCountryUSA = false) {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const skipValidation = ["address2", "firstName", "lastName"];

  if (validateKey) {
    buyFlowStore.showPaymentFormErrors[validateKey] = validateAddressFormFields(
      validateKey,
      buyFlowStore.paymentFormAddress[validateKey],
      isCountryUSA
    );
  } else {
    for (const key in buyFlowStore.paymentFormAddress) {
      //skip form items that are not required
      if (skipValidation.indexOf(key) >= 0) {
        continue;
      }

      //check for valid format
      buyFlowStore.showPaymentFormErrors[key] = validateAddressFormFields(
        key,
        buyFlowStore.paymentFormAddress[key],
        isCountryUSA
      );
    }
  }

  const paymentFormHasErrors = Object.values(buyFlowStore.showPaymentFormErrors).some(l => l.length > 0);
  buyFlowManagerStore.paymentFormHasErrors = paymentFormHasErrors;
  return paymentFormHasErrors;
}

export function validateAddressFormFields(key: string, value: string, isCountryUSA = false) {
  let response: string[] = [];

  switch (key) {
    case "firstName":
      response = isFirstNameValid(value);
      break;
    case "lastName":
      response = isLastNameValid(value);
      break;
    case "state":
      response = isStateValid(value);
      break;
    case "address1":
      response = isAddress1Valid(value);
      break;
    case "city":
      response = isCityValid(value);
      break;
    case "zipcode":
      response = isZipValid(value, isCountryUSA);
      break;
  }

  return response;
}

export async function checkValidations() {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const userStore = useUserStore();
  const emailPageStore = useEmailPageStore();
  const paymentPageStore = usePaymentPageStore();

  //Check shipping address
  if (!buyFlowStore.shippingMatchesBilling) {
    checkShippingFormForErrors();
  } else {
    buyFlowManagerStore.shippingFormHasErrors = false;
  }

  //Check payment address
  if (buyFlowStore.paymentType === paymentTypes.CreditCard) {
    //If the user is selecting a saved payment method we don't need to check for form errors
    if (userStore.isEstablishedAccount && paymentPageStore.selectedPaymentMethodId) {
      buyFlowManagerStore.paymentFormHasErrors = false;
    } else {
      checkPaymentFormForErrors();
    }
  }

  //Check Email exists
  if (!userStore.email && !userStore.currentUser) {
    emailPageStore.dirtyFlagEmail = true;
    buyFlowManagerStore.emailHasErrors = true;
  }
}

export async function zuoraCreditCardFormSuccessHandler(event) {
  const paymentPageStore = usePaymentPageStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const buyFlowStore = useBuyFlowStore();

  try {
    buyFlowManagerStore.iFrameHasErrors = false;
    buyFlowStore.updateCreditCard(event.detail.creditCardId);
    logEvent("zuoraCreditCardFormSuccessHandler", "BuyBaseHelper.ts", event.detail);
    paymentPageStore.selectedPaymentMethodId = event.detail.creditCardId;
    paymentPageStore.addedNewCreditCard = true;
    await goToPageTwo();
  } catch (err) {
    logException(err as Error, "zuoraCreditCardFormSuccessHandler in BuyBaseHelper.ts");
  }
}

export async function zuoraCreditCardFormFailureHandler(error) {
  const buyFlowManagerStore = useBuyFlowManagerStore();
  buyFlowManagerStore.iFrameHasErrors = true;
  logEvent("zuoraCreditCardFormFailureHandler", "BuyBaseHelper.ts", error);
}

export function handlePaymentMethodError(error: string) {
  const paymentPageStore = usePaymentPageStore();
  const notificationsStore = useNotificationsStore();
  const buyFlowStore = useBuyFlowStore();

  logEvent("handlePaymentMethodError", "BuyBaseHelper.ts", error);
  notificationsStore.clearNotifications();
  notificationsStore.addNotification({ type: error as NotificationType });
  if (paymentPageStore.pageState === paymentPageStates.forceAddCard) {
    paymentPageStore.pageState = paymentPageStates.addCard;
  } else {
    paymentPageStore.pageState =
      buyFlowStore.creditCards && buyFlowStore.creditCards.length > 0
        ? paymentPageStates.displayCards
        : paymentPageStates.addCard;
  }
}

export function submitZuoraIframe() {
  //buyFlowStore.processingOrderMessage = tn("processingOrderMessage");
  logEvent("submitZuoraIframe", "BuyBaseHelper.ts");
  /* eslint-disable */
  //@ts-ignore
  Z.submit();
  /* eslint-enable */
}

//used by the ReviewOrder.vue
export async function goToNextPage() {
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const buyFlowStore = useBuyFlowStore();
  const paymentPageStore = usePaymentPageStore();
  const userStore = useUserStore();

  if (buyFlowManagerStore.isPageOne) {
    //Do all component checks when moving to page two (Review Order)
    await checkValidations();

    if (userStore.isEstablishedAccount) {
      if (
        buyFlowStore.paymentType === paymentTypes.CreditCard &&
        paymentPageStore.editPayment &&
        !paymentPageStore.selectedPaymentMethodId
      ) {
        if (buyFlowStore.cart) {
          buyFlowStore.cart.paymentMethodAddress = Object.assign({}, buyFlowStore.paymentFormAddress);
        }

        //Submit the iFrame then in success handler do other checks
        submitZuoraIframe();
      } else {
        await goToPageTwo();
      }
    } else {
      if (buyFlowStore.paymentType === paymentTypes.CreditCard && paymentPageStore.editPayment) {
        if (buyFlowStore.cart) {
          buyFlowStore.cart.paymentMethodAddress = Object.assign({}, buyFlowStore.paymentFormAddress);
        }

        //Submit the iFrame then in success handler do other checks
        submitZuoraIframe();
      } else {
        await goToPageTwo();
      }
    }
  } else {
    //TODO: Complete the purchase
    checkOut();
  }
}

export function assignShippingAddress(isEditBillingAddress = false) {
  const buyFlowStore = useBuyFlowStore();

  if (!buyFlowStore.cart) {
    return;
  }

  if (buyFlowStore.shippingMatchesBilling) {
    const paymentMethod = buyFlowStore.creditCards.find(c => c.id === buyFlowStore.cart?.paymentMethodId);
    if (paymentMethod?.address && !isEditBillingAddress) {
      setShippingAddressFromCC(paymentMethod);
    } else if (buyFlowStore.cart?.paymentMethodAddress) {
      //Get the name off the payment method and stick it in the first name
      const address = Object.assign({} as IUserAddress, buyFlowStore.cart.paymentMethodAddress, {
        firstName: paymentMethod?.accountHolder ?? "",
      });
      buyFlowStore.cart.shippingAddress = address;
    } else {
      //default to address in store that is not on the cart
      const address = Object.assign({} as IUserAddress, buyFlowStore.paymentFormAddress, {
        firstName: paymentMethod?.accountHolder ?? "",
      });
      buyFlowStore.cart.shippingAddress = address.address1 ? address : undefined;
    }
  } else {
    // get Shipping address from store
    buyFlowStore.cart.shippingAddress = Object.assign({} as IUserAddress, buyFlowStore.shippingAddress);
  }
}

export async function goToPageTwo() {
  const buyFlowManagerStore = useBuyFlowManagerStore();
  const paymentPageStore = usePaymentPageStore();
  const buyFlowStore = useBuyFlowStore();

  //If there are components with errors don't go forward.
  if (!buyFlowManagerStore.allComponentsErrorFree) {
    return;
  }

  //Reload Payment methods if new one was added
  if (paymentPageStore.addedNewCreditCard) {
    buyFlowStore.creditCards = (await loadPaymentMethods()) || [];
    paymentPageStore.addedNewCreditCard = false;
  }

  paymentPageStore.editPayment = false;

  buyFlowStore.updateShoppingCartTotal();
  router.push({ path: buyFlowPageTwoRoute, query: router.currentRoute.value.query });
}

export function setShippingFirstLastFromPaymentMethod() {
  const buyFlowStore = useBuyFlowStore();

  if (!buyFlowStore.cart || !buyFlowStore.shippingMatchesBilling) {
    return;
  }

  //Set shipping address first/last name
  if (buyFlowStore.shippingMatchesBilling && buyFlowStore.cart.shippingAddress) {
    const paymentMethod = buyFlowStore.creditCards.find(c => c.id === buyFlowStore.cart?.paymentMethodId);
    const nameSplit = paymentMethod?.accountHolder?.split(" ") || [];
    if (nameSplit.length > 0) {
      const firstName = nameSplit[0] || "";
      let lastName = "";

      // If there are more than 2 names, treat the first as first name and the rest as last name
      nameSplit.shift(); // Remove first name
      lastName = nameSplit.join(" "); // Join the rest as last name

      buyFlowStore.cart.shippingAddress.firstName = firstName;
      buyFlowStore.cart.shippingAddress.lastName = lastName;
    }
  }
}

export async function checkOut() {
  addBuyFlowLogEntry(`Complete purchase clicked`);
  // Prevents popups after purchase
  hideAllWisepopsPopups();
  const notificationsStore = useNotificationsStore();
  const buyFlowStore = useBuyFlowStore();

  try {
    notificationsStore.clearNotifications();
    if (buyFlowStore.cart) {
      buyFlowStore.showProcessingOrderMessage = true;
      buyFlowStore.cart.userGuid = buyFlowStore.userGuid;
      logEvent("checkout-buyFlowStore.cart", "BuyBaseHelper.ts", buyFlowStore.cart);

      //Set shipping address first/last name
      setShippingFirstLastFromPaymentMethod();

      if (buyFlowStore.cart.brand === openTextBrands.Webroot) {
        bazaarVoiceInitialize(bazaarVoiceScriptWebrootUrl, openTextBrands.Webroot);
      } else if (buyFlowStore.cart.brand === openTextBrands.Carbonite) {
        bazaarVoiceInitialize(bazaarVoiceScriptCarboniteUrl, openTextBrands.Carbonite);
      }

      //Complete purchase
      buyFlowStore.cart = (await unifiedApi.purchaseShoppingCart(buyFlowStore.cart)).data;
      // wisepops only tracks revenue if the wisepops popup discount was accepted
      if (FLAGS.ENABLE_WISEPOPS === "true" && buyFlowStore.wisepopsBrand == openTextBrands.Webroot) {
        setWisepopsRevenue(buyFlowStore.cart.total);
      }

      if (buyFlowStore.cart && buyFlowStore.cart.encryptedEmail && buyFlowStore.cart.encryptionKeyId) {
        if (buyFlowStore.cart.brand === openTextBrands.Webroot) {
          bvWebrootEventHandler(buyFlowStore.cart);
        } else if (buyFlowStore.cart.brand === openTextBrands.Carbonite) {
          bvCarboniteEventHandler(buyFlowStore.cart);
        }
      }

      //Retrieve keycode after a fixed number of seconds
      await new Promise(resolve => setTimeout(resolve, PURCHASE_KEYCODE_DELAY_IN_SECOND));
      //function to retrieve.once received the keycodes are stored in buyFlowStore.PurchaseKeycodes
      await fetchPurchasedKeycodes();
      cartUpdate(buyFlowStore.cart, googleTagCartEvents.purchase);
      //await awaitAsyncPurchaseProcess();
      buyFlowStore.showProcessingOrderMessage = false;
      //displayAutoRenewalDialog.value = false;
      const itemWithId = buyFlowStore.cart?.items.find(l => l.computerId > 0);
      if (itemWithId) {
        buyFlowStore.recentPurchase = { computerId: itemWithId.computerId, purchasedOnMilliseconds: Date.now() };
      }
      setTimeout(() => router.push({ path: "/buy/orderConfirmation" }), 500);
    } else {
      //Throw an error. This should never happen
      //buyFlowStore.showProcessingOrderMessage = false;
      throw new Error("cart is undefined");
    }
  } catch (error) {
    buyFlowStore.showProcessingOrderMessage = false;
    const err = error as AxiosError;
    const response = err?.response?.data as IPurchaseError;
    addBuyFlowLogEntry(`Complete purchase failed: error ${response?.message}`);

    //Redirect if user is under maintenance
    if (response && response?.message === purchaseErrors.UnderMaintenance) {
      router.push("/underMaintenance");
      return;
    }

    if (response.message && response.message === purchaseErrors.HasOutstandingInvoices) {
      redirectToOutstandingInvoice();
      return;
    }

    if (response) {
      handlePurchaseError(response.message, err?.response?.status);
    } else {
      notificationsStore.addNotification({ type: "PurchaseFailureError", dismissible: false });
    }

    logException(error as Error, `BuyBaseHelp.ts: processPurchase`);
  }
}

export async function fetchPurchasedKeycodes() {
  const buyFlowStore = useBuyFlowStore();
  try {
    if (buyFlowStore.cart != null && buyFlowStore.cart?.invoiceNumbers != null) {
      const invoiceNumber = buyFlowStore.cart.invoiceNumbers[0];
      const OrderCode = buyFlowStore.cart?.orderCode;
      if (OrderCode != null) {
        const purchaseKeycodes: IPurchaseKeycode[] = (await unifiedApi.getPurchaseKeycodes(invoiceNumber, OrderCode))
          .data;
        purchaseKeycodes.forEach(p => {
          const itemInCart = buyFlowStore.cart?.items.filter(n =>
            n.subscriptionNumbers.some(s => s === p.subscriptionNumber)
          );
          if (itemInCart != null) {
            p.sku = itemInCart[0].sku;
          }
        });
        buyFlowStore.PurchaseKeycodes = purchaseKeycodes;
      }
    }
  } catch (error) {
    logException(error as Error, `BuyBaseHelp.ts: fetchPurchasedKeycodes`);
  }
}

export function getCreditCardsOnFile() {
  const ccs: IPaymentMethod[] = [];
  const buyFlowStore = useBuyFlowStore();
  if (buyFlowStore.creditCards.length > 0) {
    let paymentMethods = buyFlowStore.creditCards;
    paymentMethods = paymentMethods.filter(n => n.payPalGatewayName !== payPalGateways.CarbonitePayPalGateway);

    const defaultCC = paymentMethods.find(card => card.defaultPaymentMethod) ?? null;

    const otherCCs =
      sortArrayOfObjects(
        paymentMethods.filter(
          c => !c.defaultPaymentMethod && c.payPalGatewayName !== payPalGateways.WebrootPayPalGateway
        ),
        "expirationDate",
        "descending"
      ) ?? null;

    const payPalMethods =
      sortArrayOfObjects(
        paymentMethods.filter(
          c => !c.defaultPaymentMethod && c.payPalGatewayName === payPalGateways.WebrootPayPalGateway
        ),
        "expirationDate",
        "descending"
      ) ?? null;

    if (defaultCC) ccs.push(defaultCC);
    for (const cc of otherCCs) {
      ccs.push(cc);
    }
    for (const pp of payPalMethods) {
      ccs.push(pp);
    }
  }
  return ccs;
}

export function isPaymentAddressEmpty() {
  const buyFlowStore = useBuyFlowStore();
  const skipValidation = ["country"];
  let isEmpty = true;
  for (const key in buyFlowStore.paymentFormAddress) {
    //skip form items that are not required
    if (skipValidation.indexOf(key) >= 0) {
      continue;
    }
    if (buyFlowStore.paymentFormAddress[key].length > 0) {
      isEmpty = false;
    }
  }

  return isEmpty;
}

export function setPaymentType(type: string) {
  addBuyFlowLogEntry(`Payment ${type} radio button clicked`);
  const paymentPageStore = usePaymentPageStore();
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();

  buyFlowStore.paymentType = type;
  paymentPageStore.currentPaymentType = type;
  paymentPageStore.selectedPaymentMethodId = "";
  paymentPageStore.paymentMethodFromFile = false;
  if (buyFlowStore.cart) {
    buyFlowStore.cart.paymentMethodId = "";
  }

  buyFlowManagerStore.completeStep(layoutItemNames.paymentAddress);
  //If the user switched back to credit card after adding PayPal the billing address step was already set to complete so it is showing a blank address
  if (type === paymentTypes.CreditCard && buyFlowManagerStore.isStepComplete(layoutItemNames.paymentAddress)) {
    //check if payment address is empty on the cart and the store
    if (isPaymentAddressEmpty() && !buyFlowStore.cart?.paymentMethodAddress) {
      buyFlowManagerStore.clearCompletedStep(layoutItemNames.paymentAddress);
      buyFlowManagerStore.setCurrentStep(layoutItemNames.paymentAddress);
    }
  }

  buyFlowManagerStore.setCurrentStep(layoutItemNames.paymentAddress);
}

export function getFlatRateUpgradeRenewalText() {
  const buyFlowStore = useBuyFlowStore();

  if (!buyFlowStore.isFlatRateUpgrade) {
    return "";
  }

  //Look up the current term of the license key
  const months =
    buyFlowStore.getRatePlanInfo(buyFlowStore.licenseKeyInfo?.ratePlanId || "")?.months || DEFAULT_TERM_LENGTH_MONTHS;
  const currentTerm = months / DEFAULT_TERM_LENGTH_MONTHS;

  //Renewal Date
  const renewDate = getRenewDate();

  //Get price for sub when it renews into the upgraded plan
  const flatRatePlan = buyFlowStore.getRatePlanInfo(buyFlowStore.selectedPlan?.ratePlanId || "");
  const upgradeRatePlanId = buyFlowStore.getRatePlanIdBySku(
    buyFlowStore.selectedPlan?.sku || "",
    months,
    flatRatePlan?.includedUnits
  );

  const upgradeRatePlan = buyFlowStore.getRatePlanInfo(upgradeRatePlanId);

  return t("ReviewOrderComponent.flatRateUpgradeRenewalText", {
    year: currentTerm,
    date: renewDate,
    amount: formatMoney(upgradeRatePlan?.catalogPrice),
  });
}

export function isUSAddress() {
  const buyFlowStore = useBuyFlowStore();
  let isUS = false;

  if (
    buyFlowStore.paymentFormAddress.country &&
    buyFlowStore.shippingMatchesBilling &&
    (buyFlowStore.paymentFormAddress.country === UNITED_STATES ||
      buyFlowStore.paymentFormAddress.country === UNITED_STATES_COUNTRY_CODE)
  ) {
    isUS = true;
  } else if (
    buyFlowStore.shippingAddress.country &&
    !buyFlowStore.shippingMatchesBilling &&
    (buyFlowStore.shippingAddress.country === UNITED_STATES ||
      buyFlowStore.shippingAddress.country === UNITED_STATES_COUNTRY_CODE)
  ) {
    isUS = true;
  }

  return isUS;
}

export function getGoogleTagManagerStep(googleTagCartEvent: googleTagCartEvents | undefined = undefined) {
  const buyFlowStore = useBuyFlowStore();
  const buyFlowManagerStore = useBuyFlowManagerStore();

  if (googleTagCartEvent) {
    return googleTagCartEvent;
  }

  if (!buyFlowManagerStore.isPageOne) {
    return buyFlowStore.pageState;
  }

  let pageState = pageStates.email;

  switch (buyFlowManagerStore.openStep.name) {
    case layoutItemNames.paymentAddress:
    case layoutItemNames.shippingAddress:
    case layoutItemNames.cardDetails:
      pageState = pageStates.payment;
      break;

    case layoutItemNames.reviewOrder:
      pageState = pageStates.reviewOrder;
      break;
  }

  return pageState;
}

export function getEnhancedCartTypeForGTM() {
  const buyFlowManagerStore = useBuyFlowManagerStore();
  let type: enhancedCartTypes | undefined = undefined;

  switch (buyFlowManagerStore.openStep.name) {
    case layoutItemNames.shippingAddress:
      type = enhancedCartTypes.shippingAddress;
      break;

    case layoutItemNames.paymentAddress:
      type = enhancedCartTypes.billingAddress;
      break;

    case layoutItemNames.cardDetails:
      type = enhancedCartTypes.cardDetails;
      break;

    case layoutItemNames.reviewOrder:
      type = enhancedCartTypes.reviewOrder;
      break;
  }

  return type;
}

//Used by BuyflowStore.updateShoppingCartTotal() to decide if it should log an event during preview.
//For enhanced cart we are going to move the GTM calls to the navigation since we don't do a preview
//call on every step
export async function pushGoogleTagManageEventWithCart(
  cart: IShoppingCart,
  googleTagCartEvent: googleTagCartEvents | undefined = undefined,
  force = false
) {
  const buyFlowManagerStore = useBuyFlowManagerStore();

  if (force || !buyFlowManagerStore.isPageOne) {
    cartUpdate(cart, getGoogleTagManagerStep(googleTagCartEvent), getEnhancedCartTypeForGTM());
  }
}

//Used for enhance cart steps since stores should not reference other stores
export async function pushGoogleTagManageEvent(googleTagCartEvent: googleTagCartEvents | undefined = undefined) {
  const buyFlowStore = useBuyFlowStore();
  if (buyFlowStore.cart) {
    pushGoogleTagManageEventWithCart(buyFlowStore.cart, googleTagCartEvent, true);
  }
}

export function mapCountryAndStateNamesToCodes(currentCountry: string, currentState: string) {
  const matchedCountry = statesAndProvincesCountryCodes.find(c => c.name === currentCountry);

  if (!matchedCountry) return [currentCountry, currentState];

  const countryCode = matchedCountry.code;
  const states = shippingStatesAndProvinces[matchedCountry.name];

  if (states?.some(c => c.text === currentState)) {
    const matchedState = states.find(c => c.text === currentState).value;
    if (matchedState) {
      currentState = matchedState;
    }
  }

  currentCountry = countryCode;
  return [currentCountry, currentState];
}
